import { List, ListItemText, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { CHECKBOX, RADIO } from '../../constants';
import { getSurveyItem } from '../../helpers/surveyUtil';
import { BlockProps, ChapterProps, SectionProps } from '../../models/global';
import ButtonComponent from '../FormComponents/Button';
import Modal from '../Modal/Modal';

interface InterrelatedFieldProps {
  chapters: ChapterProps[];
  language: string;
  forceToUpdate: () => void;
}

export type InterrelatedFieldRef = {
  handleModal: (chapterId: string, sectionId: string, tillQuestionIndex: number, disabled?: boolean) => void;
};

const InterrelatedField = forwardRef<InterrelatedFieldRef, InterrelatedFieldProps>((props, ref) => {
  const { chapters, language, forceToUpdate } = props;
  const [modalData, setModalData] = useState<{
    questions: Array<BlockProps>;
    currentQuestion: BlockProps;
    disabled?: boolean;
  }>();
  const [dependsOnField, setDependsOnField] = useState<BlockProps>();
  const [dependsOnOptions, setDependsOnOptions] = useState<string[]>([]);
  const [step, setStep] = useState(1);

  const handleModal = (chapterId: string, sectionId: string, tillQuestionIndex: number, disabled?: boolean) => {
    const section = getSurveyItem(chapters, chapterId, sectionId) as SectionProps;
    const keyOption = language === 'English' ? 'name' : `name${language}`;
    const keyQuestion = language === 'English' ? 'question' : `question${language}`;

    const optionIds =
      section.fieldBlocks[tillQuestionIndex].dependsOnOptions ||
      section.fieldBlocks[tillQuestionIndex].interrelatedFields?.map(el => el.optionId);
    if (optionIds?.length) {
      const dependentQuestion = section.fieldBlocks.find(fieldBlock => {
        const fieldBlockOptions = fieldBlock.options?.map(el => el.id) || [];
        return optionIds.some(optionId => fieldBlockOptions.includes(optionId));
      });
      if (dependentQuestion) {
        setDependsOnField(dependentQuestion);
        const dependentOptions = dependentQuestion.options?.filter(item => optionIds.includes(item.id));
        if (dependentOptions?.length) {
          setStep(2);
          dependsOnOptions.length = 0;
          dependentOptions.forEach(el => {
            dependsOnOptions.push(el.id);
          });
          setDependsOnOptions([...dependsOnOptions]);
        }
      }
    }
    setModalData({
      questions: section.fieldBlocks.filter(
        (el, i) =>
          i < tillQuestionIndex &&
          el[keyQuestion] &&
          [CHECKBOX, RADIO].includes(el.fieldType) &&
          el.options.find(option => option[keyOption]),
      ),
      currentQuestion: section.fieldBlocks[tillQuestionIndex],
      disabled,
    });
  };

  useImperativeHandle(ref, () => ({
    handleModal: (chapterId: string, sectionId: string, tillQuestionIndex: number, disabled?: boolean) => {
      handleModal(chapterId, sectionId, tillQuestionIndex, disabled);
    },
  }));

  const closeModal = () => {
    setDependsOnField(null);
    setDependsOnOptions([]);
    setModalData(null);
    setStep(1);
  };

  return (
    <Modal
      disabled={modalData?.disabled}
      open={!!modalData}
      onClose={closeModal}
      title='Interrelated field'
      subtitle={
        step === 1
          ? 'Which question is the selected question dependent on?'
          : 'Which answer is the selected question dependent on?'
      }
      button={
        step === 1 ? (
          <ButtonComponent
            text='Next'
            disabled={!dependsOnField?.id}
            onClick={() => {
              setStep(prev => prev + 1);
              setDependsOnOptions([]);
            }}
          />
        ) : (
          <ButtonComponent
            text='Done'
            onClick={() => {
              modalData.currentQuestion.dependsOnOptions = dependsOnOptions;
              forceToUpdate();
              closeModal();
            }}
          />
        )
      }
      data={
        <div>
          {step === 1 ? (
            modalData?.questions?.length ? (
              modalData.questions.map(el => (
                <Box key={el.id} display='flex' alignItems='center'>
                  <Radio
                    onClick={() => {
                      setDependsOnField(el);
                    }}
                    checked={dependsOnField?.id === el.id}
                  />
                  {language === 'English' ? el.question : `${el[`question${language}`] || ''}`}
                </Box>
              ))
            ) : (
              <Typography variant='body2' textAlign='center'>
                No valid field exists
              </Typography>
            )
          ) : (
            <Box>
              <List>
                {dependsOnField?.options?.map(el => (
                  <ListItemText key={el.id}>
                    <Checkbox
                      onClick={() => {
                        if (dependsOnOptions.includes(el.id)) {
                          setDependsOnOptions(dependsOnOptions.filter(option => option !== el.id));
                        } else {
                          setDependsOnOptions([...dependsOnOptions, el.id]);
                        }
                      }}
                      checked={dependsOnOptions.includes(el.id)}
                    />
                    {language === 'English' ? el.name : `${el[`name${language}`] || ''}`}
                  </ListItemText>
                ))}
              </List>
            </Box>
          )}
        </div>
      }
      goBack={step > 1 ? () => setStep(prev => prev - 1) : null}
    />
  );
});

InterrelatedField.displayName = 'InterrelatedField';

export default InterrelatedField;
